import { useContentAttach } from '@components/App/Modal/BlockModal'
import {
  type EditorValue,
  isEmptyValue,
  LiteEditor,
} from '@octopus-do/octopus-editor'
import { ScrollbarCls } from '@octopus-do/octopus-storybook'
import { ArrowRight } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import {
  buildMention,
  GuestName,
} from '@/components/ArtBoard/Comments/components/CommentsForm'
import { useAuth, useGuestName } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { Recipient } from '@/types'
import { clsJoin, isSubmitHotkey } from '@/utils'
import css from './styles.module.css'

interface Props {
  /**
   * `author` is the guest's committed name, or '' — for a member, and for a
   * guest who left the box empty. It is handed over rather than read from the
   * hook by the caller: committing and submitting happen in one event, and a
   * caller's own `useGuestName` still holds the value from its last render.
   */
  onSubmit: (value: EditorValue, author: string) => void
  autofocus: boolean
}

export const NewCommentForm: React.FC<Props> = ({ onSubmit, autofocus }) => {
  const [state, setState] = useState<EditorValue>([])

  const { onUpload } = useContentAttach()
  const auth = useAuth()
  // prefilled from storage rather than asked again, and shared with the comment
  // form: a guest who named themselves on a page comment is not asked on a pin
  const { commitGuestName, guestName, setGuestName } = useGuestName()

  // A guest reaching the canvas first has nothing stored to inherit, which is
  // the whole reason the field is here and not only on `CommentsForm`.
  const asGuest = !auth.authenticated

  const members: Recipient[] = useSelector(
    migrationProjectSelector.getProjectRecipients
  )

  const empty = isEmptyValue(state)

  // the artboard is off: no new thread and no reply. The card sits in the
  // canvas, which takes no pointer then, but the field holds the focus
  const artboardDisabled = useArtBoardDisabled()

  const onClick = () => {
    onSubmit(state, asGuest ? commitGuestName() : '')
    setState([])
  }

  // the editor has no onKeyDown of its own, the keydown bubbles up to here
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isSubmitHotkey(event) || empty || artboardDisabled) {
      return
    }

    event.preventDefault()
    onClick()
  }

  const mentionData = members.map(buildMention)

  return (
    <div
      className={clsJoin(css.new_comment_form, !empty && css.filled)}
      onKeyDown={onKeyDown}
    >
      {asGuest && (
        <div className={css.guest_name}>
          <GuestName value={guestName} onChange={setGuestName} />
        </div>
      )}
      <div className={clsJoin(css.field, ScrollbarCls)}>
        <div className={css.field_content}>
          <LiteEditor
            placeholder="Leave a reply..."
            autofocus={autofocus}
            onFilesUpload={onUpload}
            value={state}
            onChange={setState}
            mentions={mentionData}
            readonly={artboardDisabled}
          />
        </div>
      </div>
      <div className={clsJoin(css.footer, empty && css.hidden)}>
        <div className={css.button_wrapper}>
          <button
            onClick={onClick}
            type="button"
            className={css.send_button}
            disabled={artboardDisabled}
          >
            <ArrowRight height="16px" width="16px" />
          </button>
        </div>
      </div>
    </div>
  )
}
